import {CircularProgress} from '@material-ui/core';
import React, {useEffect, useState} from 'react';
import {useRouteMatch} from 'react-router';
import {Redirect, Route, Switch, useLocation} from 'react-router-dom';
import OAuth2Guard from '../../../components/OAuth2Guard';
import {BasicLayout} from '../../../layouts';
import productUtils from '../../../utils/products';
import {useStateWithLocalStorage} from '../utils/localStorage';
import RecruitFormView from './RecruitmentForm';
import SelfRegisterLanding from './SelfRegisterLanding/SelfRegisterLanding';

const useQuery = () => {
  const {search} = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
};

const fetchProduct = async (product) => {
  return await productUtils.getProduct(product);
};

const fetchForce = async (force, product) => {
  return await productUtils.getForce(force, product);
};

const RecruitmentRoutes = () => {
  const {path} = useRouteMatch();
  const query = useQuery();

  const [roleForceData, setRoleForceData] = useStateWithLocalStorage('roleForceData');

  const [product, setProduct] = useState(null);
  const [force, setForce] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState();

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(async () => {
    const {role: productId, force: forceId} = resolveRoleForceData();
    if (productId && forceId) {
      await fetchRoleForceData(productId, forceId);
      setLoading(false);
    } else {
      setError(400);
      setLoading(false);
    }
  }, []);

  const resolveRoleForceData = () => {
    const productId = query.get('product');
    const forceId = query.get('force');

    if (productId && forceId) {
      return {role: productId, force: forceId};
    }

    if (roleForceData) {
      return JSON.parse(roleForceData);
    }

    return {};
  };

  const fetchRoleForceData = async (productId, forceId) => {
    try {
      let [fetchedRole, fetchedForce] = await Promise.all([
        fetchProduct(productId),
        fetchForce(forceId, productId)
      ]);
      if (fetchedRole?.status === 200 && fetchedForce?.status === 200) {
        setProduct(fetchedRole.data);
        setForce(fetchedForce.data);
        setRoleForceData(JSON.stringify({role: productId, force: forceId}));
      }
    } catch (e) {
      handleError(e);
      setLoading(false);
      console.error('Error while loading product', e);
    }
  };

  const handleError = (e) => {
    if (e?.response) {
      /*
      * The request was made and the server responded with a
      * status code that falls out of the range of 2xx
      */
      if (e?.response?.status === 404) {
        setError(404);
      } else if (e?.response?.status === 422) {
        setError(422);
      } else {
        setError(500);
      }
    } else if (e?.request) {
      /*
      * The request was made but no response was received, `error.request`
      * is an instance of XMLHttpRequest in the browser and an instance
      * of http.ClientRequest in Node.js
      */
      setError(504);
    } else {
      // Something happened in setting up the request and triggered an Error
      setError(503);
    }
  };

  const processErrors = () => {
    if (error === 400) {
      return <div data-testid="error-400">Either the force or the assessment type you are looking for is incorrect. Please get in touch with your force for the correct website address.</div>;
    } else if (error === 404) {
      return <div  data-testid="error-404">Either the force or the assessment type you are looking for is incorrect. Please get in touch with your force for the correct website address.</div>;
    } else if (error === 422) {
      return <div  data-testid="error-422">The force is not currently accepting applications for this assessment. Please contact them directly for guidance.</div>;
    } else if (error === 500){
      return <div data-testid="error-500">Internal server error.</div>;
    } else if (error === 503){
      return <div data-testid="error-503">Service unavailable.</div>;
    } else if (error === 504){
      return <div data-testid="error-504">There was an error communicating with the server. Please check your network connection and retry.</div>;
    }
  };

  if (isLoading) {
    return (
      <BasicLayout translate={false}>
        <CircularProgress data-testid='loading' />
      </BasicLayout>
    );
  }

  if (error) {
    return (
      <BasicLayout translate={false}>
        {processErrors()}
      </BasicLayout>
    );
  }

  return (
    <Switch>
      <Route path={path} exact>
        <BasicLayout translate={false}>
          <SelfRegisterLanding forceName={force.name} productId={product.uniqueID} />
        </BasicLayout>
      </Route>
      <Route path={`${path}/role-force`} exact>
        <Redirect to={path} />
      </Route>
      <Route path={`${path}/:slug`}>
        <OAuth2Guard>
          <BasicLayout translate={false} withLogout>
            <RecruitFormView product={product} force={force} />
          </BasicLayout>
        </OAuth2Guard>
      </Route>
    </Switch>
  );
};

export default RecruitmentRoutes;
